/**
 * Pure classification logic shared by the frontend Performance Summary
 * calibration tab and the backend user-analytics email renderer. Both sides
 * previously reimplemented the same thresholds independently — kept here so
 * a threshold/label change can't drift between FE and BE.
 */
import { ConfidenceBiasZoneLabel } from '../enums/confidence-bias-zone-label.enum';
import { CalibrationLevel } from '../enums/calibration-level.enum';

/** Bias pct magnitude (0-50) beyond which a zone is classified as TIMID/AGGRESSIVE. */
export const CONFIDENCE_BIAS_ZONE_THRESHOLD = 10;

/** Bias pct magnitude beyond which the TIMID/AGGRESSIVE zone is considered "far". */
export const CONFIDENCE_BIAS_FAR_ZONE_THRESHOLD = 30;

/**
 * Clamps a raw confidence-bias decimal (mean(probability - outcome), -1..1)
 * to -0.5..0.5 and scales it to a -50..50 pct, matching the gauge's display range.
 */
export const getConfidenceBiasPct = (bias: number): number =>
	Math.max(-0.5, Math.min(0.5, bias)) * 100;

/**
 * Classifies a raw confidence-bias decimal into TIMID/OPTIMAL/AGGRESSIVE.
 */
export const getConfidenceBiasZoneLabel = (bias: number): ConfidenceBiasZoneLabel => {
	const pct = getConfidenceBiasPct(bias);

	if (pct < -CONFIDENCE_BIAS_ZONE_THRESHOLD) return ConfidenceBiasZoneLabel.Timid;
	if (pct > CONFIDENCE_BIAS_ZONE_THRESHOLD) return ConfidenceBiasZoneLabel.Aggressive;
	return ConfidenceBiasZoneLabel.Optimal;
};

/** Minimum settled forecasts in a calibration bin to count toward the avg gap. */
export const CALIBRATION_MIN_BIN_N = 5;

/** Minimum total settled predictions (across all bins) to show the calibration chart at all. */
export const CALIBRATION_MIN_TOTAL_SETTLED = 5;

/** Minimum settled forecasts in a category to surface a real RBP gap instead of "-". */
export const MIN_CATEGORY_FORECASTS_FOR_GAP = 10;

/** Avg |hitRate - avgPredicted| gap below which calibration is "elite". */
export const CALIBRATION_ELITE_THRESHOLD = 0.07;

/** Avg gap below which calibration is "good" (vs. "developing" above it). */
export const CALIBRATION_GOOD_THRESHOLD = 0.15;

/**
 * Classifies calibration quality from bins by the average gap between actual
 * hit rate and average predicted probability, across bins with enough data.
 */
export const getCalibrationLevel = (
	bins: { n: number; hitRate: number | null; avgPredicted: number | null }[]
): CalibrationLevel => {
	const usable = bins.filter(
		(b) => b.n >= CALIBRATION_MIN_BIN_N && b.hitRate != null && b.avgPredicted != null
	);

	if (usable.length === 0) return CalibrationLevel.Insufficient;

	const avgGap =
		usable.reduce(
			(sum, b) => sum + Math.abs((b.hitRate as number) - (b.avgPredicted as number)),
			0
		) / usable.length;

	if (avgGap < CALIBRATION_ELITE_THRESHOLD) return CalibrationLevel.Elite;
	if (avgGap < CALIBRATION_GOOD_THRESHOLD) return CalibrationLevel.Good;
	return CalibrationLevel.Developing;
};

/** Tone copy for each calibration level. Single source shared by FE and BE. */
export const CALIBRATION_TONES: Record<CalibrationLevel, string> = {
	[CalibrationLevel.Insufficient]: 'Not enough settled forecasts yet to assess calibration.',
	[CalibrationLevel.Elite]:
		"You're tracking the line — your confidence consistently matches reality. That's elite calibration.",
	[CalibrationLevel.Good]:
		"You're close to the line. A handful of buckets drift, but on the whole your confidence matches outcomes.",
	[CalibrationLevel.Developing]:
		"There's room to tighten — when you say 70%, the world doesn't always agree. Calibration is the skill that scales.",
};
